/* eslint-disable react/prop-types */
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useCallback, useEffect, useState } from "react";
import { Eye, Trash2, PenLine } from "lucide-react";
import { Link } from "react-router-dom";
import { deleteStaff } from "@/api/new-api/staff-management/staff/staff";
import { toast } from "react-hot-toast";
import {
  DeleteConfirmDialog,
  ViewShiftDetails,
} from "../../dialog/staff-manage/all-staff-dialog";
import hasPermission from "@/hooks/hasPermission";
import useAuth from "@/hooks/useAuth";
import API from "@/api/lib/api";

export function   AllStaffTable({ tableData, setTableData, refreshStats }) {
  const [selectedId, setSelectedId] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [shiftDetails, setShiftDetails] = useState(null);
  const [shiftId, setShiftId] = useState(null);
  const [openShiftDialog, setOpenShiftDialog] = useState(false);
  const [personToDelete, setPersonToDelete] = useState(null);

  const openDialog = (id, name) => {
    setSelectedId(id);
    setPersonToDelete(name);
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setPersonToDelete(null);
  };

  const onShiftDialog = (data) => {
    setShiftDetails(data.shift_schedule);
    setShiftId(data.unique_id || data.id);
    setOpenShiftDialog(true);
  };

  const offshiftdialog = () => {
    setOpenShiftDialog(false);
  };

  const deleteStaffHandler = async (id) => {
    try {
      if (!id) {
        toast.error("Staff ID is required to delete.");
        return;
      }

      const response = await deleteStaff(id);

      if (response?.status === "success") {
        toast.success("Staff deleted successfully");

        setTableData((prevData) => prevData.filter((item) => item.id !== id));
        if (refreshStats) {
          refreshStats();
        }
      } else {
        toast.error("Failed to delete staff:", response.message);
      }
    } catch (error) {
      toast.error(
        error.message || "Something went wrong while deleting staff.",
      );
    } finally {
      closeDialog();
    }
  };

  // Manage toggle states for each staff
  const [enabledStates, setEnabledStates] = useState({});

  useEffect(() => {
    if (tableData?.length > 0) {
      const initialStates = {};
      tableData.forEach((person) => {
        initialStates[person.id] = person?.user?.status === "active";
      });
      setEnabledStates(initialStates);
    }
  }, [tableData]);

  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const handleToggle = useCallback(
    async (id) => {
      if (!user?.restaurant_id) return;

      setLoading(true);
      try {
        const { data } = await API.get(`staff-status-update/${id}`);

        const updatedValue = data?.data.staff?.user?.status;
        const isNowEnabled = updatedValue === "active";

        // Update this particular staff’s toggle state
        setEnabledStates((prev) => ({
          ...prev,
          [id]: isNowEnabled,
        }));

        toast.success(
          `Status updated to ${isNowEnabled ? "Active" : "Inactive"}`,
        );
        if (refreshStats) {
          refreshStats();
        }
      } catch (error) {
        console.error("Toggle failed:", error);
        toast.error("Failed to update status.");
      } finally {
        setLoading(false);
      }
    },
    [user?.restaurant_id, refreshStats],
  );

  return (
    <>
      <Table className="w-full">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[140px]">ID</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Designations</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>Join Date</TableHead>
            <TableHead>Shift</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tableData?.length == 0 && (
            <TableRow>
              <TableCell colSpan="8" className="text-center">
                No staff found
              </TableCell>
            </TableRow>
          )}
          {tableData?.length > 0 &&
            tableData?.map((person) => (
              <TableRow key={person?.id}>
                <TableCell className="font-medium">
                  {person?.unique_id || "N/A"}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {/* <img
                    src={person.image}
                    alt={person.name}
                    className='w-8 h-8 rounded-full'
                  /> */}
                    <span>{person?.user?.first_name || ""} {person?.user?.last_name || ""}</span>
                  </div>
                </TableCell>
                <TableCell>{person?.designation || "N/A"}</TableCell>
                <TableCell>{person?.phone || "N/A"}</TableCell>
                <TableCell>{person?.joining_date || "N/A"}</TableCell>
                <TableCell>
                  {/* {person.shift} */}
                  <Eye
                    onClick={() => onShiftDialog(person)}
                    size={18}
                    className="cursor-pointer"
                  />
                </TableCell>
                <TableCell>
                  <button
                    onClick={() => handleToggle(person?.id)}
                    disabled={loading}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-300 ${
                      enabledStates[person?.id] ? "bg-green-600" : "bg-gray-500"
                    } ${loading ? "opacity-70 cursor-not-allowed" : ""}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300 ${
                        enabledStates[person?.id]
                          ? "translate-x-5"
                          : "translate-x-"
                      }`}
                    />
                  </button>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-2 justify-end">
                    {hasPermission("back_office.staff_management.staff_details") && (
                      <Link
                        to={`/backoffice/staff-management/staff/profile/${person?.id}`}
                      >
                        <Eye size={18} className="cursor-pointer" />
                      </Link>
                    )}
                    {hasPermission("back_office.staff_management.staff_update") && (
                      <Link
                        to={`/backoffice/staff-management/staff/update/${person?.id}`}
                      >
                        <PenLine
                          size={18}
                          className="cursor-pointer text-cyellow"
                        />
                      </Link>
                    )}
                    {hasPermission("back_office.staff_management.staff_delete") && (
                      <div onClick={() => openDialog(person?.id, person?.name)}>
                        <Trash2
                          size={18}
                          className="cursor-pointer text-cred "
                        />
                      </div>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
      <DeleteConfirmDialog
        isOpen={isDialogOpen}
        onClose={closeDialog}
        onConfirm={() => {
          deleteStaffHandler(selectedId);
        }}
        personName={personToDelete || ""}
      />

      <ViewShiftDetails
        isOpen={openShiftDialog}
        onClose={offshiftdialog}
        shiftId={shiftId}
        shifts={shiftDetails}
      />
    </>
  );
}
